import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import { getDateYMD } from '@vakantiesnl/services/src/util/dateUtils';
import FlatPickr from 'react-flatpickr';
import { GlobalStyles } from 'tss-react';

import { cssStyles } from './DatePicker.style';
import { type DateValue } from './DatePicker.types';
import { getDatePickerOptions } from './datePickerHelpers';
import { DepartureDateRangeSwitch } from '../../molecules/DepartureDateRangeSwitch';
import { DepartureRangeConfirmation } from '../../molecules/DepartureRangeConfirmation';
import { FlexibleDepartureDate } from '../../molecules/FlexibleDapartureDate';
import { useNewStyles } from '../../molecules/MultiRoomPartyPicker/MultiRoomPartyPicker.style';

/** Globally import the basic css from Flatpickr **/
import 'flatpickr/dist/flatpickr.css';

export type Props = {
	onSelectDate: (value: DateValue) => void;
	selectedValue: DateValue;
	initialValue: DateValue;
	hasFlexibilityOptions?: boolean;
};

export const DatePicker: FC<Props> = ({ selectedValue, onSelectDate, initialValue, hasFlexibilityOptions }) => {
	const { theme } = useNewStyles();
	const memoizedCssStyles = useMemo(() => cssStyles(theme), [theme]);

	const flatPickrRef = useRef<FlatPickr>(null);

	const [mode, setMode] = useState(selectedValue.mode);
	const [flexDays, setFlexDays] = useState(selectedValue.mode === 'single' ? selectedValue.flexDays : 0);

	const onChangeDate = useCallback(
		(date: Date[]) => {
			if (mode === 'single') {
				onSelectDate({ mode: 'single', value: date, flexDays });
			} else {
				onSelectDate({ mode: 'range', value: date });
			}
		},
		[mode, flexDays, onSelectDate],
	);

	const onChangeMode = useCallback(
		(mode: 'single' | 'range') => {
			flatPickrRef.current?.flatpickr.clear();

			// Reset value back to initial value when user switches modes back and forth
			if (mode === initialValue.mode) {
				flatPickrRef.current?.flatpickr.setDate(initialValue.value, true);
				initialValue.mode === 'single' && setFlexDays(initialValue.flexDays);
			}

			setMode(mode);
		},
		[initialValue],
	);

	const onChangeFlexDays = useCallback(
		(flexDays: number) => {
			setFlexDays(flexDays);

			if (selectedValue.mode === 'single') {
				onSelectDate({ ...selectedValue, flexDays });
			}
		},
		[onSelectDate, selectedValue],
	);

	const flatPickrOptions = useMemo(() => ({ ...getDatePickerOptions(), mode }), [mode]);

	const defaultValue = !!selectedValue.value.length
		? getDateYMD(selectedValue.value[selectedValue.value.length - 1])
		: undefined;

	return (
		<>
			<GlobalStyles styles={memoizedCssStyles} />
			{hasFlexibilityOptions && (
				<DepartureDateRangeSwitch calendarMode={mode} onToggleButtonSelect={onChangeMode} />
			)}
			<FlatPickr
				options={flatPickrOptions}
				onChange={onChangeDate}
				value={selectedValue.value ?? undefined}
				ref={flatPickrRef}
				defaultValue={defaultValue}
			/>
			{hasFlexibilityOptions && mode === 'single' && (
				<FlexibleDepartureDate
					flexibleDepartureDateButtonsActive={!!selectedValue.value.length}
					flexibleDepartureDate={flexDays}
					onFlexDepartureDateSelect={onChangeFlexDays}
				/>
			)}
			{hasFlexibilityOptions && mode === 'range' && (
				<DepartureRangeConfirmation dateRange={selectedValue.value} />
			)}
		</>
	);
};
